import React, { useState } from "react";

import { useGetVimeoData, useUserContext } from "utils";

import { COUNTRY_CODE } from "consts";

import { Flex, FullScreenOverlay, GatedVimeo, Icon, LazyLoadImage, Typography } from "components";

export type WidgetVideoProps = {
  vimeoUrlOrId: string;
  title: string;
  description?: string;
  allowedCountries?: COUNTRY_CODE[];
};

const isFullVimeoUrl = (value: string) => value.includes("vimeo.com") || value.startsWith("http");

export const WidgetVideo = ({ vimeoUrlOrId, title, description, allowedCountries }: WidgetVideoProps) => {
  const { user } = useUserContext();
  const isFullUrl = isFullVimeoUrl(vimeoUrlOrId);
  const { formattedDuration, thumbnailUrl, title: vimeoVideoTitle, isLoading: isLoadingVimeoData } = useGetVimeoData(vimeoUrlOrId, isFullUrl);

  const [showVideoOverlay, setShowVideoOverlay] = useState(false);

  const showVideo = !allowedCountries || (!!user?.farm?.country && allowedCountries.includes(user.farm.country as COUNTRY_CODE));

  if (!showVideo) {
    return null;
  }

  return (
    <>
      <Flex className="widget-video-container" align="center" gap="1rem">
        <div
          className="widget-video-thumbnail"
          onClick={() => setShowVideoOverlay(true)}
          onKeyDown={(e) => e.key === "Enter" && setShowVideoOverlay(true)}
          role="button"
          tabIndex={0}
        >
          {isLoadingVimeoData ? (
            <div className="skeleton-background" />
          ) : (
            <LazyLoadImage
              src={thumbnailUrl || "/images/misc/play-image.svg"}
              alt={vimeoVideoTitle || "video thumbnail"}
              width="100%"
              height="100%"
              delay={100}
            />
          )}
          <div className="play-button">
            <Icon iconName="tabler/player-play" color="#fff" fill="#fff" size="xl" />
          </div>
        </div>
        <Flex vertical justify="center">
          <Typography variant="paragraph-s" bold className="margin-0">
            {title}
          </Typography>
          {description && (
            <Typography variant="paragraph-s" className="margin-0">
              {description}
            </Typography>
          )}
          <Flex align="center" gap="0.25rem" className="margin-top-50">
            <Icon iconName="tabler/clock" size="s" />
            <Typography variant="paragraph-s" className="margin-0">
              {isLoadingVimeoData ? "..." : formattedDuration}
            </Typography>
          </Flex>
        </Flex>
      </Flex>
      <FullScreenOverlay
        dataTestId="widget-video-overlay"
        showModal={showVideoOverlay}
        onClose={() => setShowVideoOverlay(false)}
        wrapClassName="full-screen-overlay widget-video-overlay-wrapper"
        maskClosable={true}
      >
        <div className="widget-video-overlay-video-container">
          {showVideoOverlay && <GatedVimeo video={vimeoUrlOrId} autoplay responsive showByline={false} showPortrait={false} showTitle={false} />}
        </div>
      </FullScreenOverlay>
    </>
  );
};
